'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import Link from 'next/link';
import Image from 'next/image';
import { logger } from '@/utils/logger';
import styles from '@/styles/Blog.module.css';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  featured_image: string;
  author: string;
  published_at: string;
  tags: string[];
}

function BlogContent() {
  const searchParams = useSearchParams();
  const search = searchParams?.get('search') || ''; // Handle search parameter
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        setLoading(true);
        const response = await fetch('/api/blog');
        
        if (!response.ok) {
          throw new Error('Failed to fetch blog posts');
        }
        
        const { success, posts: blogPosts } = await response.json();
        setPosts(blogPosts || []);
      } catch (err) {
        logger.error('Error fetching blog posts', { error: err });
        setError('Failed to load blog posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  return (
    <>
      {/* Hero Section */}
      <section className={styles.hero}>
        <div className={styles.container}>
          <h1>Our Blog</h1>
          <p>Insights, updates, and stories from TimeLee Cares</p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className={styles.blogPosts}>
        <div className={styles.container}>
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
            </div>
          ) : error ? (
            <div className="text-center py-8 text-red-500">
              {error}
            </div>
          ) : posts.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No blog posts available at this time. Please check back later.
            </div>
          ) : (
            <div className={styles.postsGrid}>
              {posts.map((post) => (
                <Link 
                  key={post.id} 
                  href={`/blog/${post.slug}`}
                  className={styles.postCardLink}
                  aria-label={`Read full blog post: ${post.title}`}
                >
                  <article className={styles.postCard}>
                    <div className={styles.postImage}>
                      {post.featured_image ? (
                        <Image
                          src={post.featured_image}
                          alt={post.title}
                          width={400}
                          height={250}
                          className={styles.featuredImg}
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/images/blog-placeholder.jpg';
                          }}
                        />
                      ) : (
                        <div className={styles.imagePlaceholder}>
                          <span>Blog Image</span>
                        </div>
                      )}
                    </div>
                    <div className={styles.postContent}>
                      <div className={styles.postMeta}>
                        <time dateTime={post.published_at} className={styles.postDate}>
                          {new Date(post.published_at).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                          })}
                        </time>
                        <span className={styles.postAuthor}>By {post.author}</span>
                      </div>
                      <h2 className={styles.postTitle}>{post.title}</h2>
                      <p className={styles.postExcerpt}>{post.excerpt}</p>
                      {post.tags && post.tags.length > 0 && (
                        <div className={styles.postTags}>
                          {post.tags.map((tag, index) => (
                            <span key={index} className={styles.tag}>
                              #{tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <span className={styles.readMore}>Read More</span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export default function BlogPage() {
  return (
    <Suspense fallback={
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    }>
      <BlogContent />
    </Suspense>
  );
}

